import { useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CreditCard } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface PaymentFormProps {
  amount: number;
  onPaymentMethodCreated: (paymentIntentId: string) => void;
  isSubmitting: boolean;
}

export const PaymentForm = ({ amount, onPaymentMethodCreated, isSubmitting }: PaymentFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string>("");
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError("");

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError("Card element not found");
      setProcessing(false);
      return;
    }

    try {
      // Create payment intent on the server
      const { data, error: functionError } = await supabase.functions.invoke('create-payment-intent', {
        body: { amount: Math.round(amount * 100) } // Convert to cents
      });

      if (functionError) throw functionError;

      const { clientSecret, paymentIntentId } = data;

      // Confirm the payment setup (but don't capture yet)
      const { error: confirmError } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
        },
        setup_future_usage: 'off_session',
      });

      if (confirmError) {
        setError(confirmError.message || "Payment setup failed");
      } else {
        onPaymentMethodCreated(paymentIntentId);
      }
    } catch (err) {
      setError("Failed to process payment information");
      console.error("Payment error:", err);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Card className="border-border/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-primary" />
          Payment Information
        </CardTitle>
        <CardDescription>
          Your card will be charged when a coach accepts your booking
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-4 border border-border/40 rounded-lg bg-background">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: "16px",
                  color: "hsl(var(--foreground))",
                  "::placeholder": {
                    color: "hsl(var(--muted-foreground))",
                  },
                },
              },
            }}
          />
        </div>
        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}
        <Button
          onClick={handleSubmit}
          disabled={!stripe || processing || isSubmitting}
          className="w-full"
          size="lg"
        >
          {processing ? "Processing..." : "Confirm Payment Method"}
        </Button>
      </CardContent>
    </Card>
  );
};
